/**
 * Cashier dashboard — admin-styled tabbed layout, scoped to this cashier's
 * customer sub-group. Same shell as the operator/admin dashboards.
 *
 * Tabs (admin can toggle via Settings → Role Access):
 *   today        - Daily report tiles
 *   players      - Only this cashier's owned + unclaimed players
 *   cashouts     - Pending cashout queue for their group
 *   transactions - Every txn for their group (date + bucket + search)
 *   reports      - Operator/store weekly card (read-only)
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import RoleDashboardLayout, { useDashboardPermissions } from '../components/RoleDashboardLayout';
import DailyReportTiles from '../components/DailyReportTiles';
import OperatorWeeklyCard from '../components/OperatorWeeklyCard';
import PendingCashoutsPanel from '../components/PendingCashoutsPanel';
import AllCashoutsPanel from '../components/AllCashoutsPanel';
import { PlayerDrilldownProvider } from '../components/PlayerDrilldownContext';
import TransactionsTab from '../components/TransactionsTab';
import PlayersTab from '../components/PlayersTab';
import FloorPulseStrip from '../components/FloorPulseStrip';
import LoadsByMethodCard from '../components/LoadsByMethodCard';
import SquareReceiptsQueueCard from '../components/admin/SquareReceiptsQueueCard';
import AfterHoursAutoCreditCard from '../components/admin/AfterHoursAutoCreditCard';
import ForgotPasswordInbox from '../components/ForgotPasswordInbox';
import { SquareReceiptsProvider } from '../contexts/SquareReceiptsContext';
import { StaffAlertsProvider } from '../contexts/StaffAlertsContext';
import StaffAlertsBell from '../components/StaffAlertsBell';
import PlatformCreditsTile from '../components/PlatformCreditsTile';
import AutoCredit247Badge from '../components/AutoCredit247Badge';
import CloseShiftButton from '../components/CloseShiftButton';
import ScopedFinancialReport from '../components/ScopedFinancialReport';
import SettleNowCard from '../components/SettleNowCard';
import { AdminLotteryHelperPanel } from './admin/AdminLotteryHelperPanel';
import { WheelsAdminPanel } from './admin/WheelsAdminPanel';
import CashierLotteryTab from '../components/CashierLotteryTab';
import { Sun, Users, Inbox, Activity, FileText, Ticket, Sparkles } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CashierDashboard = () => {
    const { logout } = useAuth();
    const allowed = useDashboardPermissions();
    const [me, setMe] = useState(null);

    useEffect(() => {
        axios.get(`${API}/cashier/me`, { withCredentials: true })
            .then(({ data }) => setMe(data))
            .catch(() => { });
    }, []);

    const canCashout = me?.can_cashout !== false;
    const dailyLimit = me?.daily_load_limit || 0;
    const remaining = dailyLimit > 0 ? Math.max(0, dailyLimit - (me?.loaded_today || 0)) : null;

    const tabs = [
        { value: 'today',        label: 'Today',        icon: Sun,      allowedKey: 'today' },
        { value: 'players',      label: 'My players',   icon: Users,    allowedKey: 'players' },
        { value: 'cashouts',     label: 'Cashouts',     icon: Inbox,    allowedKey: 'cashouts' },
        { value: 'lottery',      label: 'Lottery',      icon: Ticket,   allowedKey: 'lottery' },
        { value: 'wheels',       label: 'Wheels',       icon: Sparkles, allowedKey: 'wheels' },
        { value: 'transactions', label: 'Transactions', icon: Activity, allowedKey: 'transactions' },
        { value: 'reports',      label: 'Reports',      icon: FileText, allowedKey: 'reports' },
    ];

    return (
        <PlayerDrilldownProvider>
        <StaffAlertsProvider>
        <SquareReceiptsProvider>
        <RoleDashboardLayout
            title={me?.name || 'Cashier'}
            subtitle={me?.operator?.name ? `${me.operator.name} · cashier console` : 'Cashier console'}
            rolePill="CASHIER"
            rightLabel={dailyLimit > 0 ? 'Daily load limit' : null}
            rightValue={dailyLimit > 0 ? `$${(me?.loaded_today || 0).toFixed(0)} / $${dailyLimit.toFixed(0)}` : null}
            headerExtras={<><PlatformCreditsTile /><ForgotPasswordInbox /><StaffAlertsBell /></>}
            tabs={tabs}
            allowed={allowed}
            onLogout={logout}
            children={{
                today: (
                    <div className="space-y-4">
                        <FloorPulseStrip />
                        <div className="flex justify-end -mb-2"><AutoCredit247Badge /></div>
                        <SquareReceiptsQueueCard />
                        <AfterHoursAutoCreditCard mode="cashier" />
                        <DailyReportTiles />
                        <LoadsByMethodCard />
                        <CloseShiftButton />
                        {dailyLimit > 0 && (
                            <p className="text-xs text-zinc-500" data-testid="daily-limit-pill">
                                Daily load limit <span className="text-zinc-300 font-mono">${(me?.loaded_today || 0).toFixed(2)}</span> / ${dailyLimit.toFixed(2)}
                                {remaining !== null && <span className="text-amber-300"> · ${remaining.toFixed(2)} remaining</span>}
                            </p>
                        )}
                    </div>
                ),
                players: <PlayersTab showCreateButton={true} canCashout={canCashout} />,
                cashouts: (
                    <div className="space-y-6">
                        <PendingCashoutsPanel role="cashier" />
                        <AllCashoutsPanel role="cashier" />
                    </div>
                ),
                lottery: <CashierLotteryTab />,
                wheels: <WheelsAdminPanel role="cashier" />,
                transactions: <TransactionsTab endpoint="/cashier/transactions" />,
                reports: (
                    <div className="space-y-6">
                        <SettleNowCard />
                        <ScopedFinancialReport endpoint="/cashier/reports/financial" />
                        <OperatorWeeklyCard />
                    </div>
                ),
            }}
        />
        </SquareReceiptsProvider>
        </StaffAlertsProvider>
        </PlayerDrilldownProvider>
    );
};

export default CashierDashboard;
